// ============================================
// ConverseTech Frontend - Dashboard Page
// ============================================

import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Flame, Clock, Trophy, BarChart3, Play, Settings, User, Target, CheckCircle, Zap } from 'lucide-react';
import { getDashboardOverview, getTodayRitual, RitualResponse } from '../lib/api';
import RadarChart from '../components/RadarChart';
import SessionHistoryItem from '../components/SessionHistoryItem';
import type { DashboardOverview } from '@conversetech/shared';
import { TRAINING_MODE_LABELS } from '@conversetech/shared';

// Mock UserButton for testing
function UserButton() {
  return (
    <button className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
      <User className="w-4 h-4 text-gray-500" />
    </button>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();

  const { data: overview, isLoading, error } = useQuery<DashboardOverview>({
    queryKey: ['dashboard'],
    queryFn: getDashboardOverview,
  });

  const { data: ritualData } = useQuery<RitualResponse>({
    queryKey: ['ritual'],
    queryFn: getTodayRitual,
  });

  if (error) {
    return (
      <div data-testid="error-state" className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Failed to load dashboard</p>
          <button onClick={() => window.location.reload()} className="btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 safe-area-inset-top">
        <div className="container mx-auto px-4 py-3 sm:py-4 flex justify-between items-center">
          <h1 className="text-lg sm:text-xl font-bold text-gray-900">Dashboard</h1>
          <div className="flex items-center gap-3 sm:gap-4">
            <Link to="/settings" className="text-gray-500 hover:text-gray-700 p-2 -m-2">
              <Settings className="w-5 h-5" />
            </Link>
            <UserButton />
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 sm:py-8 safe-area-inset-x" data-testid="dashboard">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <StatCard
            testId="streak-card"
            valueTestId="streak-value"
            icon={<Flame className="w-5 h-5 text-orange-500" />}
            label="Streak"
            value={isLoading ? '-' : `${overview?.streak ?? 0}`}
            suffix="days"
          />
          <StatCard
            testId="minutes-7d-card"
            valueTestId="minutes-7d-value"
            icon={<Clock className="w-5 h-5 text-blue-500" />}
            label="Minutes (7d)"
            value={isLoading ? '-' : `${overview?.minutes_spoken_7d ?? 0}`}
          />
          <StatCard
            testId="det-score-card"
            valueTestId="det-score-value"
            icon={<Trophy className="w-5 h-5 text-yellow-500" />}
            label="DET Score"
            value={isLoading ? '-' : `${overview?.estimated_det_score ?? 0}`}
          />
          <StatCard
            testId="sessions-completed-card"
            valueTestId="sessions-completed-value"
            icon={<BarChart3 className="w-5 h-5 text-green-500" />}
            label="Sessions"
            value={isLoading ? '-' : `${overview?.sessions_completed ?? 0}`}
          />
        </div>

        {/* Daily Ritual Card */}
        {ritualData?.ritual && (
          <RitualCard
            ritual={ritualData.ritual}
            recommendation={ritualData.recommendation}
            onStart={() => navigate(`/session?mode=${ritualData.ritual.recommended_mode}`)}
          />
        )}

        {/* Start Training Button */}
        <div className="mb-6 sm:mb-8">
          <button
            data-testid="start-training-button"
            onClick={() => navigate('/session')}
            className="w-full py-4 bg-primary-600 text-white rounded-xl text-base sm:text-lg font-medium hover:bg-primary-700 active:bg-primary-800 transition-colors flex items-center justify-center gap-2 touch-target"
          >
            <Play className="w-5 h-5" />
            {ritualData?.ritual?.completed_at ? 'Start Another Session' : 'Start Today\'s Training'}
          </button>
        </div>

        {/* Radar Chart and History */}
        <div className="grid gap-6 sm:gap-8 md:grid-cols-2">
          {/* Skills Radar Chart */}
          <div className="card p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Skills Overview</h2>
            <div data-testid="skills-radar-chart">
              {overview?.skills ? (
                <RadarChart skills={overview.skills} />
              ) : (
                <div className="h-48 sm:h-64 flex items-center justify-center text-gray-400">
                  {isLoading ? 'Loading...' : 'No data yet'}
                </div>
              )}
            </div>
          </div>

          {/* Session History */}
          <div className="card p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Recent Sessions</h2>
            <div data-testid="session-history" className="space-y-2 sm:space-y-3">
              {overview?.recent_sessions && overview.recent_sessions.length > 0 ? (
                overview.recent_sessions.map((session) => (
                  <SessionHistoryItem key={session.id} session={session} />
                ))
              ) : (
                <div data-testid="empty-state" className="text-center py-8 text-gray-500">
                  {isLoading ? 'Loading...' : 'No sessions yet. Start your first training!'}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function StatCard({
  testId,
  valueTestId,
  icon,
  label,
  value,
  suffix,
}: {
  testId: string;
  valueTestId: string;
  icon: React.ReactNode;
  label: string;
  value: string;
  suffix?: string;
}) {
  return (
    <div data-testid={testId} className="card p-3 sm:p-4">
      <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
        {icon}
        <span className="text-xs sm:text-sm text-gray-500 truncate">{label}</span>
      </div>
      <div className="flex items-baseline gap-1">
        <span data-testid={valueTestId} className="text-xl sm:text-2xl font-bold text-gray-900">
          {value}
        </span>
        {suffix && <span className="text-xs sm:text-sm text-gray-500">{suffix}</span>}
      </div>
    </div>
  );
}

const PRIORITY_STYLES = {
  high: {
    bg: 'bg-gradient-to-r from-orange-500 to-red-500',
    icon: <Zap className="w-5 h-5" />,
    label: 'Priority',
  },
  medium: {
    bg: 'bg-gradient-to-r from-blue-500 to-purple-500',
    icon: <Target className="w-5 h-5" />,
    label: 'Recommended',
  },
  low: {
    bg: 'bg-gradient-to-r from-green-500 to-teal-500',
    icon: <CheckCircle className="w-5 h-5" />,
    label: 'Optional',
  },
};

function RitualCard({
  ritual,
  recommendation,
  onStart,
}: {
  ritual: RitualResponse['ritual'];
  recommendation?: RitualResponse['recommendation'];
  onStart: () => void;
}) {
  const isCompleted = !!ritual.completed_at;
  const priority = recommendation?.priority || 'medium';
  const priorityStyle = PRIORITY_STYLES[priority];

  return (
    <div data-testid="ritual-card" className="mb-8">
      <div className={`${priorityStyle.bg} rounded-xl p-6 text-white shadow-lg`}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            {priorityStyle.icon}
            <span className="text-sm font-medium uppercase tracking-wide opacity-90">
              Today's {priorityStyle.label} Training
            </span>
          </div>
          {isCompleted && (
            <span className="flex items-center gap-1 text-sm bg-white/20 px-2 py-1 rounded-full">
              <CheckCircle className="w-4 h-4" />
              Completed
            </span>
          )}
        </div>

        <h2 className="text-2xl font-bold mb-2">
          {TRAINING_MODE_LABELS[ritual.recommended_mode]}
        </h2>

        <div className="flex items-center gap-4 mb-4 text-sm opacity-90">
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {ritual.recommended_duration} min
          </span>
          {ritual.focus_area && (
            <span className="flex items-center gap-1">
              <Target className="w-4 h-4" />
              {ritual.focus_area}
            </span>
          )}
        </div>

        {recommendation?.reason && (
          <p className="text-sm opacity-80 mb-4">{recommendation.reason}</p>
        )}

        {!isCompleted && (
          <button
            data-testid="start-ritual-button"
            onClick={onStart}
            className="w-full py-3 bg-white text-gray-900 rounded-lg font-medium hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
          >
            <Play className="w-5 h-5" />
            Start Now
          </button>
        )}
      </div>
    </div>
  );
}
